import React, { useState, useEffect } from 'react';
import CurrencyInput from 'react-currency-input-field';
import { Icon } from '@iconify/react';
import styles from './card-form.module.css';
import iconoTarjeta from './assets/icono-tarjeta.png';
import iconoUsuario from './assets/icono-usuario.png';
import iconoCalendario from './assets/icono-calendario.png';
import iconoCvv from './assets/icono-cvv.png';
import iconoCandado from './assets/icono-candado.png';
import iconoDeposito from '../../assets/icono-boton-depositar.png';
import iconoMonto from '../../assets/icono-saldo.png';
import usePaymentStore from '../../store/use-payment-store';
import useUserContext from '../../../../../hooks/useUserContext';
import numeral from 'numeral';
import WarningCard from './warning-card';


const defaultFieldErrors = {
  cardNumber: '',
  expiration: '',
  csc: '',
  holderName: '',
  holderLastName: '',
};

export const CardFormFlat = ({
  onSubmit,
  submitButtonText = 'Depositar',
  isSubmitting = false,
  disabled = false,
  submitButtonClassName = '',
  logoBrand = null,
  fieldErrors = defaultFieldErrors,
}) => {
  const data = useUserContext();
  const maxDeposit = data?.userLoyalty?.lastLevel?.deposit_limit / 100 ?? 0;
  const minAmount = 50;
  const maxAmount = maxDeposit;
  const { amount, setAmount } = usePaymentStore();
  const [saveCard, setSaveCard] = useState(false);
  const [isAmountFocused, setIsAmountFocused] = useState(false);
  const [amountError, setAmountError] = useState('');
  const [expirationMonth, setExpirationMonth] = useState('');
  const [expirationYear, setExpirationYear] = useState('');
  const expirationInputRef = React.useRef(null);

  // Handler para el cambio del monto usando CurrencyInput
  const handleAmountChange = (value, name, values) => {
    if (value === undefined || value === null || value === '') {
      setAmount(null);
      setAmountError('');
      return;
    }

    const numValue = values && values.float !== undefined ? values.float : null;

    if (numValue === null || numValue === undefined || isNaN(numValue)) {
      setAmount(null);
      setAmountError('');
      return;
    }

    setAmount(numValue);

    let error = '';
    if (numValue <= 0) {
      error = `El monto debe ser mayor a ${numeral(minAmount).format('$0,000')}`;
    } else if (numValue < minAmount) {
      error = `El monto mínimo es de ${numeral(minAmount).format('$0,000')}`;
    } else if (numValue > maxAmount && maxAmount > 0) {
      error = `El monto máximo es de ${numeral(maxAmount).format('$0,000')}`;
    }

    setAmountError(error);
  };

  const validateAmount = (value) => {
    const numValue = typeof value === 'string' ? parseFloat(value) : Number(value);

    if (!value || isNaN(numValue) || numValue <= 0) {
      return `El monto debe ser mayor a ${numeral(minAmount).format('$0,000')}`;
    }
    if (numValue < minAmount) {
      return `El monto mínimo es de ${numeral(minAmount).format('$0,000')}`;
    }
    if (numValue > maxAmount && maxAmount > 0) {
      return `El monto máximo es de ${numeral(maxAmount).format('$0,000')}`;
    }
    return '';
  };

  const getExpirationDisplay = () => {
    const month = expirationMonth || '';
    const year = expirationYear || '';

    if (!month && !year) return '';

    if (month && !year) {
      if (month.length === 1) return month;
      const formatted = month.padStart(2, '0');
      return formatted === '00' ? '' : formatted;
    }

    if (month === '00') return '';
    const formattedMonth = month.length === 1 ? month : month.padStart(2, '0');
    if (formattedMonth === '00') return '';
    return `${formattedMonth} / ${year}`;
  };

  const handleExpirationChange = (e) => {
    const value = (e.target.value || '').replace(/\D/g, '').slice(0, 4);
    const month = value.slice(0, 2);
    const year = value.slice(2, 4);

    setExpirationMonth(month);
    setExpirationYear(year);
  };

  // Mantener el cursor al final del campo expiración después de que React actualice el DOM
  useEffect(() => {
    if (!expirationInputRef.current) return;
    const display = (() => {
      const month = expirationMonth || '';
      const year = expirationYear || '';
      if (!month && !year) return '';
      if (month && !year) {
        if (month.length === 1) return month;
        const formatted = month.padStart(2, '0');
        return formatted === '00' ? '' : formatted;
      }
      if (month === '00') return '';
      const formattedMonth = month.length === 1 ? month : month.padStart(2, '0');
      if (formattedMonth === '00') return '';
      return `${formattedMonth} / ${year}`;
    })();
    if (display.length > 0) {
      expirationInputRef.current.setSelectionRange(display.length, display.length);
    }
  }, [expirationMonth, expirationYear]);

  const handleSubmit = (e) => {
    e.preventDefault();

    const amountErrorMsg = validateAmount(amount);
    if (amountErrorMsg) {
      setAmountError(amountErrorMsg);
      return;
    }
    setAmountError('');

    if (onSubmit) {
      onSubmit(e);
    }
  };

  return (
    <form id="payment-form" className={styles.form} onSubmit={handleSubmit} style={{ position: 'relative' }}>
      <input type="hidden" name="saveCard" value={saveCard ? '1' : '0'} />
      <div className={styles.fieldGroup}>
        <label className={styles.label} htmlFor="amount">
          Monto a depositar
        </label>
        <div className={`${styles.inputWrapper} ${amountError ? styles.inputWrapperError : ''}`}>
          <img src={iconoMonto} alt="Monto" className={styles.icon} />
          <CurrencyInput
            id="amount"
            name="amount"
            className={styles.input}
            value={amount && amount !== '' && amount !== 0 && amount !== null ? amount : undefined}
            onValueChange={handleAmountChange}
            onFocus={() => setIsAmountFocused(true)}
            onBlur={() => setIsAmountFocused(false)}
            decimalsLimit={2}
            {...(!isAmountFocused && { fixedDecimalLength: 2 })}
            decimalSeparator="."
            groupSeparator=","
            prefix=""
            placeholder="0,000.00"
            allowDecimals={true}
            allowNegativeValue={false}
          />
        </div>
        <small id="amount-error" className={styles.errorMessage} role="alert">
          {amountError}
        </small>
      </div>

        <div className={styles.row}>
          <div className={`${styles.fieldGroup} ${styles.fieldGroupHalf}`}>
            <label className={styles.label} htmlFor="card-holder-name">
              Nombre
            </label>
            <div className={`${styles.inputWrapper} ${fieldErrors?.holderName ? styles.inputWrapperError : ''}`}>
              <img src={iconoUsuario} alt="Usuario" className={styles.icon} />
              <input
                autoFocus
                type="text"
                id="card-holder-name"
                className={styles.input}
                placeholder="Nombre"
                autoComplete="cc-given-name"
                name="cc-given-name"
              />
            </div>
            <small id="card-holder-name-error" className={styles.errorMessage} role="alert">
              {fieldErrors?.holderName ?? ''}
            </small>
          </div>

          <div className={`${styles.fieldGroup} ${styles.fieldGroupHalf}`}>
            <label className={styles.label} htmlFor="card-holder-last-name">
              Apellidos
            </label>
            <div className={`${styles.inputWrapper} ${fieldErrors?.holderLastName ? styles.inputWrapperError : ''}`}>
              <img src={iconoUsuario} alt="Usuario" className={styles.icon} />
              <input
                type="text"
                id="card-holder-last-name"
                className={styles.input}
                placeholder="Apellidos"
                autoComplete="cc-additional-name"
                name="cc-additional-name"
              />
            </div>
            <small id="card-holder-last-name-error" className={styles.errorMessage} role="alert">
              {fieldErrors?.holderLastName ?? ''}
            </small>
          </div>
        </div>

      <div className={styles.fieldGroup}>
        <label className={styles.label} htmlFor="card-number">
          Número de tarjeta
        </label>
        <div className={`${styles.inputWrapper} ${fieldErrors?.cardNumber ? styles.inputWrapperError : ''}`}>
          <img src={iconoTarjeta} alt="Tarjeta" className={styles.icon} />
          <input
            type="text"
            id="card-number"
            className={styles.input}
            placeholder="1234 1234 1234 1234"
            autoComplete="cc-number"
            inputMode="numeric"
            name="cc-number"
            maxLength={16}
          />
        </div>
        <small id="card-number-error" className={styles.errorMessage} role="alert">
          {fieldErrors?.cardNumber ?? ''}
        </small>
      </div>

      <div className={styles.row}>
        <div className={`${styles.fieldGroup} ${styles.fieldGroupHalf}`}>
          <label className={styles.label} htmlFor="card-expiration">
            Vencimiento
          </label>
          <div className={`${styles.inputWrapper} ${fieldErrors?.expiration ? styles.inputWrapperError : ''}`}>
            <img src={iconoCalendario} alt="Calendario" className={styles.icon} />
            <input
              ref={expirationInputRef}
              type="text"
              id="card-expiration"
              className={styles.input}
              placeholder="MM / YY"
              autoComplete="cc-exp"
              name="cc-exp"
              inputMode="numeric"
              value={getExpirationDisplay()}
              onChange={handleExpirationChange}
              maxLength={7}
            />
          </div>
          <small id="card-expiration-error" className={styles.errorMessage} role="alert">
            {fieldErrors?.expiration ?? ''}
          </small>
        </div>

        <div className={`${styles.fieldGroup} ${styles.fieldGroupHalf}`}>
          <label className={styles.label} htmlFor="card-csc">
            CVV
          </label>
          <div className={`${styles.inputWrapper} ${fieldErrors?.csc ? styles.inputWrapperError : ''}`}>
            <img src={iconoCvv} alt="CVV" className={styles.icon} />
            <input
              type="password"
              id="card-csc"
              className={styles.input}
              placeholder="000"
              autoComplete="cc-csc"
              inputMode="numeric"
              name="cc-csc"
            />
          </div>
          <small id="card-csc-error" className={styles.errorMessage} role="alert">
            {fieldErrors?.csc ?? ''}
          </small>
        </div>
      </div>

      <div className={styles.securityMessage}>
        <img src={iconoCandado} alt="Candado" className={styles.icon} />
        <span className={styles.securityText}>
          Tus datos estan protegidos con encriptación de nivel bancario
        </span>
      </div>

      {logoBrand && (
        <img src={logoBrand} alt="Logo de la marca" className={styles.logoBrand} />
      )}

      <button
        type="submit"
        className={submitButtonClassName || 'payment-button'}
        disabled={isSubmitting || disabled}
      >
        {isSubmitting ? (
          <>
            <Icon icon="eos-icons:loading" className={styles.spinner} />
            Procesando...
          </>
        ) : (
          <>
            {amount && amount !== '' && amount > 0
              ? `${submitButtonText} ${numeral(amount).format('$0,0')}`
              : submitButtonText}
            <img src={iconoDeposito} alt="Deposito" className={styles.icon} />
          </>
        )}
      </button>
      <WarningCard />
    </form>
  );
};
